import React, { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import ArchiveIcon from "@mui/icons-material/Archive";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import { PostStatusChip } from "../components/PostStatusChip";
import { PublishedByBadge } from "../components/PublishedByBadge";
import { AdminTable, ActionCell } from "../../../common/adminTable";
import { useConfirm } from "../../../common/confirmDialog/useConfirm";
import { fetchPosts, updatePostStatus, deletePost, setPage, setFilters } from "../blogSlice";
import { inputSx } from "../blogTheme";
import { formatCount } from "../../../utils/formatCount";

const STATUS_OPTIONS = ["all", "draft", "published", "scheduled", "archived"];

export default function PostList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const confirm = useConfirm();

  const { posts, total, page, loading, error, filters } = useSelector((state) => state.blog);
  const [selected, setSelected] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const rowsPerPage = 20;

  const loadPosts = (pg = page) => {
    const params = { page: pg, limit: rowsPerPage };
    if (filters.search) params.search = filters.search;
    if (filters.status && filters.status !== "all") params.status = filters.status;
    if (filters.category) params.category = filters.category;
    if (filters.tag) params.tag = filters.tag;
    if (filters.featured) params.featured = true;
    dispatch(fetchPosts(params));
  };

  useEffect(() => {
    const s = searchParams.get("search") || "";
    const st = searchParams.get("status") || "all";
    dispatch(setFilters({ search: s, status: st }));
  }, []);

  useEffect(() => { loadPosts(1); }, [filters]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    dispatch(setFilters({ search: val }));
    setSearchParams((prev) => { val ? prev.set("search", val) : prev.delete("search"); return prev; });
  };

  const handleStatusChange = (e) => {
    const val = e.target.value;
    dispatch(setFilters({ status: val }));
    setSearchParams((prev) => { val !== "all" ? prev.set("status", val) : prev.delete("status"); return prev; });
  };

  const handleSelect = (id, checked) =>
    setSelected((prev) => checked ? [...prev, id] : prev.filter((x) => x !== id));

  const handleSelectAll = (checked) =>
    setSelected(checked ? posts.map((p) => p._id) : []);

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: "Delete Post",
      description: "Are you sure you want to delete this post?",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    setDeletingId(id);
    await dispatch(deletePost(id));
    setDeletingId(null);
  };

  const handleBulkDelete = async () => {
    const ok = await confirm({
      title: "Delete Selected Posts",
      description: `Delete ${selected.length} selected post(s)?`,
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    await Promise.all(selected.map((id) => dispatch(deletePost(id))));
    setSelected([]);
  };

  const columns = [
    {
      field: "title",
      headerName: "Title",
      minWidth: 220,
      cellSx: { maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
      renderCell: (row) => row.title,
    },
    {
      field: "category",
      headerName: "Category",
      width: 130,
      renderCell: (row) => row.category?.name || "—",
    },
    {
      field: "status",
      headerName: "Status",
      width: 110,
      renderCell: (row) => <PostStatusChip status={row.status} />,
    },
    {
      field: "views",
      headerName: "Views",
      width: 70,
      renderCell: (row) => formatCount(row.views),
    },
    {
      field: "publishedAt",
      headerName: "Published",
      width: 120,
      cellSx: { whiteSpace: "nowrap" },
      renderCell: (row) => row.publishedAt ? dayjs(row.publishedAt).format("MMM D, YYYY") : "—",
    },
    {
      field: "author",
      headerName: "Author",
      width: 150,
      renderCell: (row) => <PublishedByBadge user={row.author} />,
    },
    {
      field: "__actions",
      headerName: "",
      width: 110,
      align: "right",
      renderCell: (row) => (
        <ActionCell
          actions={[
            {
              label: "Edit",
              icon: <EditIcon fontSize="small" />,
              onClick: () => navigate(`/blog/posts/${row._id}/edit`),
            },
            row.status === "archived"
              ? {
                  label: "Publish",
                  icon: <PublishedWithChangesIcon fontSize="small" />,
                  onClick: async () => {
                    const ok = await confirm({
                      title: "Publish Post",
                      description: `Publish "${row.title}"?`,
                      confirmText: "Publish",
                    });
                    if (!ok) return;
                    await dispatch(updatePostStatus({ id: row._id, status: "published" }));
                    loadPosts();
                  },
                }
              : {
                  label: "Archive",
                  icon: <ArchiveIcon fontSize="small" />,
                  onClick: async () => {
                    const ok = await confirm({
                      title: "Archive Post",
                      description: `Archive "${row.title}"?`,
                      confirmText: "Archive",
                      destructive: true,
                    });
                    if (!ok) return;
                    await dispatch(updatePostStatus({ id: row._id, status: "archived" }));
                    loadPosts();
                  },
                },
            {
              label: "Delete",
              icon: <DeleteIcon fontSize="small" />,
              color: "#EA001B",
              onClick: () => handleDelete(row._id),
              loading: deletingId === row._id,
            },
          ]}
        />
      ),
    },
  ];

  return (
    <Box sx={{ px: 3, pt: 1.5, pb: 3 }}>
      <Toolbar disableGutters sx={{ justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: "#362B24", fontFamily: "Heebo" }}>
          Posts
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          {selected.length > 0 && (
            <Button
              variant="outlined"
              startIcon={<DeleteIcon />}
              onClick={handleBulkDelete}
              sx={{
                borderRadius: "29px",
                border: "1px solid #EA001B",
                color: "#EA001B",
                fontFamily: "Heebo",
                textTransform: "none",
                "&:hover": { bgcolor: "rgba(234,0,27,0.04)" },
              }}
            >
              Delete ({selected.length})
            </Button>
          )}
          <TextField
            value={filters.search}
            onChange={handleSearchChange}
            placeholder="Search posts..."
            size="small"
            sx={{ width: 200, ...inputSx }}
          />
          <TextField
            value={filters.status}
            onChange={handleStatusChange}
            size="small"
            select
            sx={{ width: 140, ...inputSx }}
          >
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </MenuItem>
            ))}
          </TextField>
          <Button
            startIcon={<AddIcon />}
            onClick={() => navigate("/blog/posts/new")}
            sx={{
              background: "linear-gradient(180deg, #065F46 0%, #0D7B5C 100%)",
              color: "#fff",
              borderRadius: "29px",
              boxShadow: "1px 2px 30px 0px rgba(0,0,0,0.06)",
              fontFamily: "Heebo",
              textTransform: "none",
              px: 3,
              "&:hover": { opacity: 0.9 },
            }}
          >
            New Post
          </Button>
        </Box>
      </Toolbar>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <AdminTable
        columns={columns}
        rows={posts}
        loading={loading}
        rowKey="_id"
        emptyText="No posts found"
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={total}
        onPageChange={(pg) => { dispatch(setPage(pg)); loadPosts(pg); }}
        selected={selected}
        onSelect={handleSelect}
        onSelectAll={handleSelectAll}
        deletingId={deletingId}
      />
    </Box>
  );
}
